import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    CardHeader,
    Button,
    Form,
    Label,
    Input,
    FormFeedback,
    Spinner,
    Alert,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Badge,
} from "reactstrap";
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import SwitchInput from "../../common/SwitchInput";
import { getLocations, createLocation } from "../../helpers/apis";
import {
    addNewFarmhouse,
    fetchFarmhouseDetails,
    updateFarmhouseDetails,
    uploadFarmhouseImages,
    addFarmhouseAmenities,
    updateFarmhouseStatus,
    deleteFarmhouseImage,
    updateImagePriorities,
} from "../../slices/farmhouse/thunk";
import { clearFarmhouseDetails } from "../../slices/farmhouse/reducer";
import { showToast } from "../../slices/toast/reducer";
import amenitiesConfig from "./amenitiesConfig.json";
import { api } from "../../config";

// Amenity groups - 修改臥室部分為動態
const AMENITY_GROUPS = {
    Common: amenitiesConfig.common || [],
    "Living Room": amenitiesConfig.livingRoom || [],
    Kitchen: amenitiesConfig.kitchen || [],
    Outdoor: amenitiesConfig.outdoor || [],
    Bathroom: amenitiesConfig.bathroom || [],
    "Bedroom": amenitiesConfig.bedroom || [],
};

const PRICE_CATEGORIES = [
    { key: "REGULAR_12HR", label: "Regular 12 Hours (7 AM - 6 PM)" },
    { key: "REGULAR_24HR", label: "Regular 24 Hours (7 PM - 6 PM Next Day)" },
    { key: "WEEKEND_12HR", label: "Weekend 12 Hours (7 AM - 6 PM)" },
    { key: "WEEKEND_24HR", label: "Weekend 24 Hours (7 PM - 6 PM Next Day)" },
];

const STORAGE_KEY = "farmhouse_create_backup";

// 表單字段配置
const FORM_FIELDS = [
    {
        section: "Basic Information",
        fields: [
            { id: "name", label: "Name *", type: "text", required: true, col: 6, placeholder: "Enter farmhouse name" },
            { id: "slug", label: "Slug", type: "text", required: false, col: 6, placeholder: "farmhouse-slug-name", disabledEdit: true },
            {
                id: "priority", label: "Priority *", type: "select", required: true, col: 6, options: [
                    { value: "", label: "Select Priority" },
                    { value: "HIGH", label: "High" },
                    { value: "MEDIUM", label: "Medium" },
                    { value: "LOW", label: "Low" }
                ]
            },
            { id: "farmNo", label: "Farm Number", type: "text", required: false, col: 6, placeholder: "e.g., Farm-1" },
            { id: "description", label: "Description", type: "textarea", required: false, col: 12, rows: 3, placeholder: "Enter farmhouse description" }
        ]
    },
    {
        section: "SEO Information",
        fields: [
            { id: "seo", label: "SEO Keywords", type: "keywords", required: false, col: 12, placeholder: "Enter keyword and press Enter or comma" }
        ]
    },
    {
        section: "Capacity & Timing",
        fields: [
            { id: "maxPersons", label: "Max Persons *", type: "number", required: true, col: 3, min: 1 },
            { id: "bedrooms", label: "Bedrooms *", type: "number", required: true, col: 3, min: 0 },
            { id: "checkInFrom", label: "Check In *", type: "time", required: true, col: 3 },
            { id: "checkOutTo", label: "Check Out *", type: "time", required: true, col: 3 }
        ]
    },
    {
        section: "Location",
        fields: [
            { id: "location.address", label: "Address *", type: "text", required: true, col: 12, placeholder: "Enter full address" },
            { id: "location.nearby", label: "Near By", type: "creatable-select", required: false, col: 4, placeholder: "Select nearby location" },
            { id: "location.city", label: "City *", type: "text", required: true, col: 4, placeholder: "Enter city" },
            { id: "location.state", label: "State *", type: "text", required: true, col: 4, placeholder: "Enter state" },
            { id: "location.mapUrl", label: "Google Maps URL", type: "map-url", required: false, col: 12, placeholder: "Paste Google Maps URL (e.g., https://www.google.com/maps/@21.1702,72.8311,15z)" },
        ]
    }
];

const PRICE_FIELDS = [
    { id: "category", label: "Category", type: "text", disabled: true, col: 3 },
    { id: "price", label: "Price (₹)", type: "number", col: 3, min: 0, step: 0.01, placeholder: "Price" },
    { id: "maxPeople", label: "Max People", type: "number", col: 3, min: 0, placeholder: "Max People" },
    { id: "extraPersonPrice", label: "Extra Person Price (₹)", type: "number", col: 3, min: 0, placeholder: "Extra Price" }
];

const FarmhouseCreate = ({ id: propId, isModal = false, onClose, onSuccess, editMode = null }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const routeParams = useParams();
    const routeId = routeParams.id;
    const id = propId || routeId;
    const isEdit = !!id;

    const { farmhouseDetails, loading, success, error } = useSelector((state) => ({
        farmhouseDetails: state.Farmhouse.farmhouseDetails,
        loading: state.Farmhouse.loading,
        success: state.Farmhouse.success,
        error: state.Farmhouse.error,
    }));

    const [selectedAmenities, setSelectedAmenities] = useState([]);
    const [serverError, setServerError] = useState("");
    const [postCreateModalOpen, setPostCreateModalOpen] = useState(false);
    const [createdFarmhouseId, setCreatedFarmhouseId] = useState(null);
    const [uploadingImages, setUploadingImages] = useState(false);
    const [savingAmenities, setSavingAmenities] = useState(false);
    const [imageFiles, setImageFiles] = useState([]);
    const [imagePreviews, setImagePreviews] = useState([]);
    const [fileTypes, setFileTypes] = useState([]); // Track file types (image/video)
    const [editModeChoice, setEditModeChoice] = useState(editMode);
    const [editChoiceModalOpen, setEditChoiceModalOpen] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [editChoiceMade, setEditChoiceMade] = useState(false);
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);
    const [currentModal, setCurrentModal] = useState(null);
    const [bedroomCount, setBedroomCount] = useState(0);
    const [locations, setLocations] = useState([]);
    const [locationsLoading, setLocationsLoading] = useState(false);
    const [creatingLocation, setCreatingLocation] = useState(false);
    const [selectedNearby, setSelectedNearby] = useState(null);
    const [createLocationModalOpen, setCreateLocationModalOpen] = useState(false);
    const [newLocationData, setNewLocationData] = useState({ nearby: "Sayan", city: "Surat", state: "Gujarat" });
    const [keywordInput, setKeywordInput] = useState("");
    const [mapExtractionStatus, setMapExtractionStatus] = useState("idle"); // idle, success, error
    const [orderedImages, setOrderedImages] = useState([]);
    const [savingPriorities, setSavingPriorities] = useState(false);
    const [isManualMode, setIsManualMode] = useState(false);
    const [manualSequence, setManualSequence] = useState([]);
    const sortedImagesRef = useRef(null);

    const normalizePriceOptions = (priceData) => {
        if (!priceData || !Array.isArray(priceData)) {
            return PRICE_CATEGORIES.map(({ key }) => ({
                category: key,
                price: 0,
                maxPeople: 0,
                extraPersonPrice: 0,
            }));
        }

        const map = {};
        priceData.forEach((item) => {
            map[item.category] = {
                price: parseFloat(item.price) || 0,
                maxPeople: item.maxPeople || 0,
                extraPersonPrice: parseFloat(item.extraPersonPrice) || 0,
            };
        });

        return PRICE_CATEGORIES.map(({ key }) => ({
            category: key,
            price: map[key]?.price || 0,
            maxPeople: map[key]?.maxPeople || 0,
            extraPersonPrice: map[key]?.extraPersonPrice || 0,
        }));
    };

    const validationSchema = Yup.object({
        name: Yup.string().required("Please enter name"),
        priority: Yup.string().required("Please select priority"),
        maxPersons: Yup.number().required("Please enter max persons").min(1, "Must be at least 1"),
        bedrooms: Yup.number().required("Please enter bedrooms").min(0, "Cannot be negative"),
        description: Yup.string(),
        checkInFrom: Yup.string().required("Please enter check-in time"),
        checkOutTo: Yup.string().required("Please enter check-out time"),
        seo: Yup.array().nullable(),
        location: Yup.object({
            address: Yup.string().required("Address is required"),
            nearby: Yup.string(),
            city: Yup.string().required("City is required"),
            state: Yup.string().required("State is required"),
            mapUrl: Yup.string().nullable(),
            latitude: Yup.string().nullable(),
            longitude: Yup.string().nullable(),
        }),
    });

    const formik = useFormik({
        initialValues: {
            name: "",
            slug: "",
            priority: "MEDIUM",
            maxPersons: 0,
            bedrooms: 0,
            description: "",
            checkInFrom: "07:00",
            checkOutTo: "18:00",
            status: false,
            isRecomanded: false,
            isAmazing: false,
            location: {
                address: "",
                nearby: "",
                city: "",
                state: "",
                mapUrl: "",
                latitude: "",
                longitude: "",
            },
            priceOptions: PRICE_CATEGORIES.map(({ key }) => ({
                category: key,
                price: 0,
                maxPeople: 0,
                extraPersonPrice: 0,
            })),
            amenities: {},
            farmNo: "",
            seo: [],
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            setServerError("");

            const formatTime = (time) => {
                if (!time) return "10:00:00";
                return time.includes(":") && time.split(":").length === 2
                    ? `${time}:00`
                    : time;
            };

            let slugToUse = values.slug || farmhouseDetails?.slug;
            if (!slugToUse && values.name) {
                const baseSlug = values.name
                    .toLowerCase()
                    .trim()
                    .replace(/[^a-z0-9\s-]/g, "")
                    .replace(/\s+/g, "-")
                    .replace(/-+/g, "-");
                const randomSuffix = Math.floor(1000 + Math.random() * 9000);
                slugToUse = `${baseSlug}-${randomSuffix}`;
            }

            const payload = {
                name: values.name.trim(),
                slug: slugToUse,
                priority: values.priority,
                maxPersons: Number(values.maxPersons) || 0,
                bedrooms: Number(values.bedrooms) || 0,
                description: values.description || "",
                checkInFrom: formatTime(values.checkInFrom),
                checkOutTo: formatTime(values.checkOutTo),
                status: isEdit ? Boolean(values.status) : false,
                isRecomanded: Boolean(values.isRecomanded),
                isAmazing: Boolean(values.isAmazing),
                location: {
                    address: values.location.address || "",
                    nearby: values.location.nearby || "",
                    city: values.location.city || "",
                    state: values.location.state || "",
                    mapUrl: values.location.mapUrl || null,
                    latitude: values.location.latitude || null,
                    longitude: values.location.longitude || null,
                },
                priceOptions: values.priceOptions
                    .filter(option => option.price > 0)
                    .map((option) => ({
                        category: option.category,
                        price: Number(option.price) || 0,
                        maxPeople: Number(option.maxPeople) || 0,
                        extraPersonPrice: Number(option.extraPersonPrice) || 0,
                    })),
                farmNo: values.farmNo || "",
                seo: Array.isArray(values.seo) ? values.seo : (values.seo ? values.seo.split(',').filter(k => k.trim() !== "") : []),
            };

            console.log("Submitting payload:", JSON.stringify(payload, null, 2));

            try {
                if (isEdit) {
                    const result = await dispatch(updateFarmhouseDetails({
                        id,
                        data: payload
                    })).unwrap();
                    console.log("Update successful:", result);

                    setTimeout(() => {
                        if (onSuccess) {
                            onSuccess();
                        }
                    }, 1000);
                } else {
                    const result = await dispatch(addNewFarmhouse(payload)).unwrap();
                    console.log("Create successful:", result);
                    if (result?.data?.id) {
                        setCreatedFarmhouseId(result.data.id);
                        setCurrentModal('amenities');
                    } else if (onSuccess) {
                        localStorage.removeItem(STORAGE_KEY);
                        onSuccess();
                    }
                }
            } catch (error) {
                console.error("API Error:", error);
                setServerError(error.message || "Failed to save farmhouse. Please check all fields.");
            }
        },
    });

    // Fetch locations
    useEffect(() => {
        const fetchLocations = async () => {
            setLocationsLoading(true);
            try {
                const response = await getLocations();
                if (!response.error && response.data) {
                    const locationsList = response.data.locations || [];
                    setLocations(locationsList);
                }
            } catch (error) {
                console.error("Error fetching locations:", error);
            } finally {
                setLocationsLoading(false);
            }
        };
        fetchLocations();
    }, []);

    useEffect(() => {
        if (isEdit && id) {
            dispatch(fetchFarmhouseDetails(id));
        } else {
            dispatch(clearFarmhouseDetails());
            formik.resetForm();
            setSelectedAmenities([]);
        }
        setInitialLoadComplete(true);
    }, [dispatch, id, isEdit]);

    useEffect(() => {
        if (isEdit && farmhouseDetails && initialLoadComplete) {
            console.log("Loaded farmhouse details:", farmhouseDetails);

            const formatTimeForInput = (time) => {
                if (!time) return "10:00";
                return time.includes(":") ? time.split(":").slice(0, 2).join(":") : time;
            };

            formik.setValues({
                ...farmhouseDetails,
                slug: farmhouseDetails.slug || "",
                checkInFrom: formatTimeForInput(farmhouseDetails.checkInFrom),
                checkOutTo: formatTimeForInput(farmhouseDetails.checkOutTo),
                priceOptions: normalizePriceOptions(
                    farmhouseDetails.priceOptions || farmhouseDetails.rent || []
                ),
                location: {
                    address: farmhouseDetails.location?.address || "",
                    nearby: farmhouseDetails.location?.nearby || "",
                    city: farmhouseDetails.location?.city || "",
                    state: farmhouseDetails.location?.state || "",
                    mapUrl: farmhouseDetails.location?.mapUrl || "",
                    latitude: farmhouseDetails.location?.latitude || "",
                    longitude: farmhouseDetails.location?.longitude || "",
                },
                status: Boolean(farmhouseDetails.status),
                isRecomanded: Boolean(farmhouseDetails.isRecomanded),
                isAmazing: Boolean(farmhouseDetails.isAmazing),
                farmNo: farmhouseDetails.farmNo || "",
                seo: Array.isArray(farmhouseDetails.seo) ? farmhouseDetails.seo : (farmhouseDetails.seo ? farmhouseDetails.seo.split(',').filter(k => k.trim() !== "") : []),
            });

            // Set bedroom count
            if (farmhouseDetails.bedrooms) {
                setBedroomCount(farmhouseDetails.bedrooms);
            }

            // Fix for amenities data structure
            if (farmhouseDetails.farmhouseAmenities && Array.isArray(farmhouseDetails.farmhouseAmenities)) {
                const normalizedAmenities = farmhouseDetails.farmhouseAmenities.map(item => ({
                    name: item.amenity?.name || '',
                    category: item.category?.name || 'Common',
                    quantity: item.quantity || 1,
                    amenityId: item.amenityId,
                    categoryId: item.categoryId
                }));
                setSelectedAmenities(normalizedAmenities);
            } else {
                setSelectedAmenities([]);
            }
        }
    }, [farmhouseDetails, isEdit, initialLoadComplete]);

    useEffect(() => {
        if (farmhouseDetails?.images && sortedImagesRef.current !== farmhouseDetails.images) {
            sortedImagesRef.current = farmhouseDetails.images;
            const sorted = [...farmhouseDetails.images].sort((a, b) => (a.priority || 0) - (b.priority || 0));
            setOrderedImages(sorted);
        }
    }, [farmhouseDetails?.images]);

    useEffect(() => {
        // Set current modal based on edit mode
        if (isModal && initialLoadComplete) {
            if (editMode === 'choice' && !editChoiceMade) {
                setCurrentModal('choice');
            } else if (editMode === 'details' || editModeChoice === 'details') {
                setCurrentModal('form');
            } else if (editMode === 'amenities_images' || editModeChoice === 'amenities_images') {
                setCurrentModal('amenities');
            } else if (!isEdit) {
                setCurrentModal('form');
            }
        }
    }, [isModal, editMode, editModeChoice, editChoiceMade, initialLoadComplete, isEdit]);

    // Update bedroom count and pricing when bedrooms field changes
    useEffect(() => {
        if (formik.values.bedrooms !== undefined) {
            setBedroomCount(Number(formik.values.bedrooms) || 0);
        }
    }, [formik.values.bedrooms]);

    // Persistent storage for Create Mode
    useEffect(() => {
        if (!isEdit) {
            const savedData = localStorage.getItem(STORAGE_KEY);
            if (savedData) {
                try {
                    const parsed = JSON.parse(savedData);
                    if (parsed.values) {
                        formik.setValues({
                            ...formik.initialValues,
                            ...parsed.values
                        });
                    }
                    if (parsed.amenities) {
                        setSelectedAmenities(parsed.amenities);
                    }
                } catch (e) {
                    console.error("Error loading saved farmhouse data", e);
                }
            }
        }
    }, [isEdit]);

    useEffect(() => {
        if (!isEdit && initialLoadComplete) {
            const dataToSave = {
                values: formik.values,
                amenities: selectedAmenities
            };
            localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave));
        }
    }, [formik.values, selectedAmenities, isEdit, initialLoadComplete]);

    // Auto-populate pricing max people when maxPersons changes
    useEffect(() => {
        const maxPersons = Number(formik.values.maxPersons) || 0;
        if (maxPersons > 0) {
            const newPriceOptions = formik.values.priceOptions.map(option => ({
                ...option,
                maxPeople: maxPersons
            }));
            formik.setFieldValue("priceOptions", newPriceOptions);
        }
    }, [formik.values.maxPersons]);

    const toggleAmenity = (category, name, checked) => {
        setSelectedAmenities((prev) => {
            if (checked) {
                if (prev.some((a) => a.name === name && a.category === category)) {
                    return prev;
                }
                return [...prev, { name, category, quantity: 1 }];
            } else {
                return prev.filter((a) => !(a.name === name && a.category === category));
            }
        });
    };

    const toggleBedroomAmenity = (bedroomNumber, amenityName, checked) => {
        const category = `Bedroom ${bedroomNumber}`;
        const name = amenityName;

        setSelectedAmenities((prev) => {
            if (checked) {
                if (prev.some((a) => a.name === name && a.category === category)) {
                    return prev;
                }
                return [...prev, { name, category, quantity: 1 }];
            } else {
                return prev.filter((a) => !(a.name === name && a.category === category));
            }
        });
    };

    const handlePriceOptionChange = (index, field, value) => {
        const newPriceOptions = [...formik.values.priceOptions];
        newPriceOptions[index] = { ...newPriceOptions[index], [field]: value };
        formik.setFieldValue("priceOptions", newPriceOptions);
    };

    const getCategoryLabel = (category) => {
        const match = PRICE_CATEGORIES.find((c) => c.key === category);
        return match ? match.label : category;
    };

    const handleStatusToggle = async (checked) => {
        try {
            await dispatch(updateFarmhouseStatus({
                id: id,
                status: checked
            })).unwrap();
        } catch (error) {
            console.error("Failed to update status:", error);
            // Revert the toggle if API fails
            formik.setFieldValue("status", !checked);
        }
    };

    const handleImageFileChange = (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        setImageFiles(prev => [...prev, ...files]);

        // Track file types
        const newFileTypes = files.map(file => {
            if (file.type.startsWith('video/')) return 'video';
            if (file.type.startsWith('image/') || file.name.toLowerCase().endsWith('.svg')) return 'image';
            return 'other';
        });
        setFileTypes(prev => [...prev, ...newFileTypes]);

        // Create previews only for images (not videos/others)
        const previewPromises = files.map((file, index) => {
            return new Promise((resolve) => {
                // If it's a video or other file, don't create a preview, just return null
                if (newFileTypes[index] !== 'image') {
                    resolve(null);
                    return;
                }

                const reader = new FileReader();
                reader.onloadend = () => {
                    resolve(reader.result);
                };
                reader.onerror = () => {
                    resolve(null);
                };
                reader.readAsDataURL(file);
            });
        });

        Promise.all(previewPromises).then(previews => {
            // Add null placeholders for videos to maintain index alignment
            const allPreviews = previews.map((preview, index) => {
                if (newFileTypes[index] === 'video') {
                    return null; // Video placeholder
                }
                return preview;
            });
            setImagePreviews(prev => [...prev, ...allPreviews]);
        });
    };

    const removeImageFile = (index) => {
        setImageFiles(prev => prev.filter((_, i) => i !== index));
        setImagePreviews(prev => prev.filter((_, i) => i !== index));
        setFileTypes(prev => prev.filter((_, i) => i !== index));
    };



    const handleOnDragEnd = (result) => {
        if (!result.destination || isManualMode) return;
        if (result.destination.index === result.source.index) return;

        setOrderedImages(prevItems => {
            const items = Array.from(prevItems);
            const [reorderedItem] = items.splice(result.source.index, 1);
            items.splice(result.destination.index, 0, reorderedItem);
            return items;
        });
    };

    const handleImageClick = (imageId) => {
        if (!isManualMode) return;

        if (manualSequence.includes(imageId)) {
            // Remove from sequence and everything after it (re-sequencing)
            const index = manualSequence.indexOf(imageId);
            setManualSequence(prev => prev.slice(0, index));
        } else {
            // Add to sequence
            setManualSequence(prev => [...prev, imageId]);
        }
    };

    const toggleManualMode = () => {
        if (isManualMode && manualSequence.length > 0) {
            // When turning off, if they had a sequence, we might want to ask or just keep it
            // For now, let's just let the "Save" button handle the final application
        }
        setIsManualMode(!isManualMode);
        if (!isManualMode) {
            setManualSequence([]);
        }
    };

    const resetManualPriorities = () => {
        setManualSequence([]);
        if (farmhouseDetails?.images) {
            const sorted = [...farmhouseDetails.images].sort((a, b) => (a.priority || 0) - (b.priority || 0));
            setOrderedImages(sorted);
        }
    };

    const getUpdatedPriorities = (targetImages = orderedImages) => {
        let finalOrder = [...targetImages];

        // If in manual mode, construct the order from the sequence first
        if (isManualMode && manualSequence.length > 0) {
            const selected = [];
            manualSequence.forEach(id => {
                const img = targetImages.find(i => i.id === id);
                if (img) selected.push(img);
            });
            const remaining = targetImages.filter(img => !manualSequence.includes(img.id));
            finalOrder = [...selected, ...remaining];
        }

        return finalOrder.map((img, index) => ({
            id: Number(img.id),
            priority: index + 1
        }));
    };

    const handleDeleteExistingImage = async (imageId) => {
        try {
            const farmhouseId = id;
            if (!farmhouseId || !imageId) return;

            await dispatch(deleteFarmhouseImage({
                farmId: farmhouseId,
                imageId: imageId
            })).unwrap();

            // Refresh farmhouse details to update images
            await dispatch(fetchFarmhouseDetails(farmhouseId));
        } catch (error) {
            console.error("Failed to delete image:", error);
            setServerError("Failed to delete image");
        }
    };

    const handleSaveAmenitiesAndImages = async () => {
        const farmhouseId = createdFarmhouseId || id;
        if (!farmhouseId) return;

        setSavingAmenities(true);
        setUploadingImages(true);

        try {
            let hasAmenities = false;
            let hasImages = false;

            if (selectedAmenities.length > 0) {
                const amenitiesToSend = selectedAmenities.map(amenity => ({
                    name: amenity.name,
                    category: amenity.category,
                    quantity: amenity.quantity
                }));

                await dispatch(addFarmhouseAmenities({
                    id: farmhouseId,
                    data: amenitiesToSend
                })).unwrap();
                hasAmenities = true;
            }

            if (imageFiles.length > 0) {
                const formData = new FormData();
                imageFiles.forEach(file => {
                    formData.append('images', file);
                });
                await dispatch(uploadFarmhouseImages({
                    id: farmhouseId,
                    data: formData
                })).unwrap();
                hasImages = true;

                if (isEdit) {
                    await dispatch(fetchFarmhouseDetails(farmhouseId));
                }
            }

            // Always sync priorities if we are in Edit mode
            if (isEdit && orderedImages.length > 0) {
                const priorities = getUpdatedPriorities();
                await dispatch(updateImagePriorities({
                    farmId: farmhouseId,
                    data: { priorities }
                })).unwrap();
            }

            // Auto-activate status after amenities/images are added - with proper error handling
            if (hasAmenities || hasImages) {
                try {
                    const result = await dispatch(updateFarmhouseStatus({
                        id: farmhouseId,
                        status: true
                    })).unwrap();
                    console.log("Status updated successfully:", result);
                    formik.setFieldValue("status", true);
                } catch (statusError) {
                    console.error("Failed to update status, but amenities/images were saved:", statusError);
                    // Still show success even if status update fails - amenities/images are saved
                }
            }

            setImageFiles([]);
            setImagePreviews([]);
            setFileTypes([]);
            setSelectedAmenities([]);

            setTimeout(() => {
                if (onSuccess) {
                    localStorage.removeItem(STORAGE_KEY);
                    onSuccess();
                }
            }, 1000);
        } catch (error) {
            console.error("Failed to save amenities/images:", error);
            setServerError("Failed to save amenities and images");
        } finally {
            setSavingAmenities(false);
            setUploadingImages(false);
        }
    };

    const handleEditChoice = (choice) => {
        setEditModeChoice(choice);
        setEditChoiceMade(true);
        if (choice === "details") {
            setCurrentModal('form');
        } else if (choice === "amenities_images") {
            setCurrentModal('amenities');
        }
    };

    const handleCloseModal = () => {
        if (onClose) onClose();
    };

    // Handle nearby location selection
    const handleNearbyChange = (selectedOption) => {
        if (selectedOption && selectedOption.value === "__create_new__") {
            // User clicked "Create New Location"
            setCreateLocationModalOpen(true);
            setSelectedNearby(null);
        } else if (selectedOption) {
            // User selected an existing location
            const nearbyLocation = locations.find(loc => loc.nearby === selectedOption.value);
            setSelectedNearby(selectedOption);
            formik.setFieldValue("location.nearby", selectedOption.value);

            // Auto-populate city and state if location exists
            if (nearbyLocation) {
                formik.setFieldValue("location.city", nearbyLocation.city || "");
                formik.setFieldValue("location.state", nearbyLocation.state || "");
            }
        } else {
            // Cleared selection
            setSelectedNearby(null);
            formik.setFieldValue("location.nearby", "");
        }
    };

    // Handle create location from modal
    const handleCreateLocation = async () => {
        const { nearby, city, state } = newLocationData;

        if (!nearby || !city || !state) {
            dispatch(showToast({ type: "error", msg: "Please fill in all required fields (Nearby, City, and State)" }));
            return;
        }

        setCreatingLocation(true);
        try {
            const response = await createLocation({
                nearby: nearby.trim(),
                city: city.trim(),
                state: state.trim()
            });

            if (!response.error) {
                // Refresh locations list using GET API
                const locationsResponse = await getLocations();
                if (!locationsResponse.error && locationsResponse.data) {
                    const updatedLocations = locationsResponse.data.locations || [];
                    setLocations(updatedLocations);

                    // Close modal and reset
                    setCreateLocationModalOpen(false);
                    setNewLocationData({ nearby: "Sayan", city: "Surat", state: "Gujarat" });

                    // Auto-fill all location fields in the form
                    formik.setFieldValue("location.nearby", nearby.trim());
                    formik.setFieldValue("location.city", city.trim());
                    formik.setFieldValue("location.state", state.trim());

                    // Show success toast message
                    dispatch(showToast({ type: "success", msg: "Location created successfully!" }));
                } else {
                    dispatch(showToast({ type: "error", msg: response.msg || "Failed to refresh locations" }));
                }
            } else {
                dispatch(showToast({ type: "error", msg: response.msg || "Failed to create location" }));
            }
        } catch (error) {
            console.error("Error creating location:", error);
            dispatch(showToast({ type: "error", msg: "Failed to create location" }));
        } finally {
            setCreatingLocation(false);
        }
    };

    // Prepare nearby options for dropdown (deduplicated by nearby value)
    const nearbyOptionsMap = new Map();
    locations
        .filter(loc => loc.nearby) // Only locations with nearby value
        .forEach(loc => {
            // Only add if we haven't seen this nearby value before
            if (!nearbyOptionsMap.has(loc.nearby)) {
                nearbyOptionsMap.set(loc.nearby, {
                    value: loc.nearby,
                    label: loc.nearby,
                    city: loc.city,
                    state: loc.state
                });
            }
        });

    const nearbyOptions = [
        ...Array.from(nearbyOptionsMap.values()),
        // Add "Create New" option at the end
        {
            value: "__create_new__",
            label: "+ Create New Location",
            isCreateOption: true
        }
    ];

    // Set selected nearby value
    useEffect(() => {
        const nearbyValue = formik.values.location?.nearby;
        if (nearbyValue) {
            const option = nearbyOptions.find(opt => opt.value === nearbyValue);
            if (option) {
                setSelectedNearby(option);
            } else {
                // If not found in options, set as custom value
                setSelectedNearby({ value: nearbyValue, label: nearbyValue });
            }
        }
    }, [formik.values.location?.nearby, locations]);

    const extractCoordinates = (url) => {
        if (!url) {
            setMapExtractionStatus("idle");
            formik.setFieldValue("location.latitude", "");
            formik.setFieldValue("location.longitude", "");
            return;
        }

        console.log("Extracting from URL:", url);

        let lat = "";
        let lng = "";
        let method = "";

        // Regular browser URL extraction
        // Pattern 1: !3dLAT!4dLONG (Place markers)
        const p1 = url.match(/!3d(-?\d+\.\d+).*!4d(-?\d+\.\d+)/);
        // Pattern 2: !1dLONG!2dLAT (Directions)
        const p2 = url.match(/!1d(-?\d+\.\d+).*!2d(-?\d+\.\d+)/);
        // Pattern 3: q=LAT,LONG (Queries)
        const p3 = url.match(/[?&]q=(-?\d+\.\d+),(-?\d+\.\d+)/);
        // Pattern 4: /place/Address/@LAT,LONG (Place info)
        const p4 = url.match(/\/place\/[^/]+\/@(-?\d+\.\d+),(-?\d+\.\d+)/);

        if (p1) {
            lat = p1[1];
            lng = p1[2];
            method = "Marker Detected";
        } else if (p2) {
            lng = p2[1];
            lat = p2[2];
            method = "Destination Detected";
        } else if (p3) {
            lat = p3[1];
            lng = p3[2];
            method = "Search Result";
        } else if (p4) {
            lat = p4[1];
            lng = p4[2];
            method = "Place Detected";
        } else {
            // Pattern 5: Camera center fallback
            const p5 = url.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
            if (p5) {
                lat = p5[1];
                lng = p5[2];
                method = "Camera Position";
            }
        }

        if (lat && lng) {
            formik.setFieldValue("location.latitude", lat);
            formik.setFieldValue("location.longitude", lng);
            setMapExtractionStatus(method);
        } else if (url.includes('google.com/maps')) {
            // If it's a valid maps URL but no coordinates found, 
            // the map preview can still search by the text in the URL
            setMapExtractionStatus("Search by Link Content");
        } else {
            setMapExtractionStatus("error");
        }
    };

    useEffect(() => {
        if (formik.values.location?.mapUrl) {
            extractCoordinates(formik.values.location.mapUrl);
        }
    }, [formik.values.location?.mapUrl]);

    const renderField = (field) => {
        const fieldId = field.id;
        const touched = fieldId.includes('.')
            ? fieldId.split('.').reduce((obj, key) => obj?.[key], formik.touched)
            : formik.touched[fieldId];

        const error = fieldId.includes('.')
            ? fieldId.split('.').reduce((obj, key) => obj?.[key], formik.errors)
            : formik.errors[fieldId];

        let fieldProps = {
            type: field.type,
            id: fieldId,
            name: fieldId,
            onChange: formik.handleChange,
            onBlur: formik.handleBlur,
            value: fieldId.includes('.')
                ? fieldId.split('.').reduce((obj, key) => obj?.[key], formik.values)
                : formik.values[fieldId],
            invalid: touched && !!error,
            placeholder: field.placeholder,
            disabled: (field.disabledEdit && isEdit) || field.disabled,
            className: field.className,
            rows: field.rows,
            min: field.min,
            step: field.step
        };

        if (field.type === 'number') {
            fieldProps.onFocus = (e) => {
                if (e.target.value === "0") {
                    formik.setFieldValue(fieldId, "");
                }
            };
            fieldProps.onBlur = (e) => {
                formik.handleBlur(e);
                if (e.target.value === "") {
                    formik.setFieldValue(fieldId, 0);
                }
            };
        }

        if (field.type === 'select') {
            return (
                <Input {...fieldProps}>
                    {field.options?.map(option => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </Input>
            );
        } else if (field.type === 'creatable-select') {
            return (
                <Select
                    isClearable
                    isSearchable
                    options={nearbyOptions}
                    value={selectedNearby}
                    onChange={handleNearbyChange}
                    placeholder={field.placeholder}
                    isLoading={locationsLoading}
                    classNamePrefix="react-select"
                    styles={{
                        control: (provided, state) => ({
                            ...provided,
                            minHeight: '38px',
                            fontSize: '14px',
                            borderColor: touched && error ? '#dc3545' : provided.borderColor,
                        }),
                        option: (provided, state) => ({
                            ...provided,
                            backgroundColor: state.data.isCreateOption
                                ? '#f8f9fa'
                                : state.isSelected
                                    ? '#007bff'
                                    : state.isFocused
                                        ? '#e9ecef'
                                        : 'white',
                            color: state.data.isCreateOption ? '#007bff' : provided.color,
                            fontWeight: state.data.isCreateOption ? '600' : 'normal',
                            cursor: 'pointer',
                        }),
                        singleValue: (provided) => ({
                            ...provided,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                        }),
                        placeholder: (provided) => ({
                            ...provided,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                        }),
                    }}
                />
            );
        } else if (field.type === 'textarea') {
            return <Input {...fieldProps} />;
        } else if (field.type === 'keywords') {
            const keywords = Array.isArray(formik.values[fieldId]) ? formik.values[fieldId] : (formik.values[fieldId] ? formik.values[fieldId].split(',').filter(k => k.trim() !== "") : []);

            const addKeyword = (val) => {
                const newKeyword = val.trim().replace(/,/g, '');
                if (newKeyword && !keywords.includes(newKeyword)) {
                    const updatedKeywords = [...keywords, newKeyword];
                    formik.setFieldValue(fieldId, updatedKeywords);
                }
                setKeywordInput("");
                formik.setFieldTouched(fieldId, true);
            };

            const removeKeyword = (indexToRemove) => {
                const updatedKeywords = keywords.filter((_, index) => index !== indexToRemove);
                formik.setFieldValue(fieldId, updatedKeywords);
                formik.setFieldTouched(fieldId, true);
            };

            return (
                <div className="keywords-container">
                    <Input
                        type="text"
                        value={keywordInput}
                        placeholder={field.placeholder}
                        onChange={(e) => setKeywordInput(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ',') {
                                e.preventDefault();
                                addKeyword(keywordInput);
                            }
                        }}
                        onBlur={() => {
                            if (keywordInput.trim()) {
                                addKeyword(keywordInput);
                            }
                        }}
                        invalid={fieldProps.invalid}
                    />
                    <div className="d-flex flex-wrap gap-2 mt-2">
                        {keywords.map((kw, index) => (
                            <span key={index} className="badge bg-soft-primary text-primary d-flex align-items-center gap-1 p-2" style={{ fontSize: '12px' }}>
                                {kw}
                                <i
                                    className="ri-close-line fs-14 cursor-pointer"
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => removeKeyword(index)}
                                ></i>
                            </span>
                        ))}
                    </div>
                </div>
            );
        } else if (field.type === 'map-url') {
            const lat = formik.values.location?.latitude;
            const lng = formik.values.location?.longitude;

            return (
                <div className="map-url-field">
                    <div className="position-relative">
                        <Input
                            {...fieldProps}
                            type="text"
                            onChange={(e) => {
                                formik.handleChange(e);
                                extractCoordinates(e.target.value);
                            }}
                        />
                        <div className="position-absolute" style={{ right: '10px', top: '50%', transform: 'translateY(-50%)', zIndex: 5 }}>
                            {mapExtractionStatus !== 'idle' && mapExtractionStatus !== 'error' && (
                                <i className="ri-checkbox-circle-fill text-success fs-18" title={mapExtractionStatus}></i>
                            )}
                            {mapExtractionStatus === 'error' && formik.values.location?.mapUrl && (
                                <i className="ri-error-warning-fill text-danger fs-18" title="Could not detect location"></i>
                            )}
                        </div>
                    </div>

                    {mapExtractionStatus === 'error' && formik.values.location?.mapUrl && (
                        <small className="text-danger mt-1 d-block">
                            Could not detect coordinates. Please check the URL format.
                        </small>
                    )}

                    {lat || lng || (mapExtractionStatus === "Search by Link Content" && formik.values.location?.mapUrl) ? (
                        <div className="mt-3 p-3 bg-light rounded border">
                            <div className="d-flex align-items-center gap-4 mb-3">
                                {lat && (
                                    <>
                                        <div>
                                            <span className="text-muted small d-block">Latitude</span>
                                            <span className="fw-medium text-dark">{lat}</span>
                                        </div>
                                        <div className="border-start ps-4">
                                            <span className="text-muted small d-block">Longitude</span>
                                            <span className="fw-medium text-dark">{lng}</span>
                                        </div>
                                    </>
                                )}
                                <div className="ms-auto d-flex align-items-center gap-2">
                                    <Badge color="success" className="bg-soft-success text-success border-success-subtle">
                                        <i className="ri-map-pin-user-line me-1"></i>
                                        {mapExtractionStatus}
                                    </Badge>
                                    <Button
                                        color="light"
                                        size="sm"
                                        className="btn-icon rounded-circle"
                                        onClick={() => {
                                            formik.setFieldValue("location.mapUrl", "");
                                            formik.setFieldValue("location.latitude", "");
                                            formik.setFieldValue("location.longitude", "");
                                            setMapExtractionStatus("idle");
                                        }}
                                        title="Clear"
                                    >
                                        <i className="ri-close-line"></i>
                                    </Button>
                                </div>
                            </div>
                            <div className="rounded overflow-hidden border shadow-sm" style={{ height: '220px' }}>
                                <iframe
                                    title="Map Preview"
                                    width="100%"
                                    height="100%"
                                    frameBorder="0"
                                    style={{ border: 0 }}
                                    allowFullScreen=""
                                    loading="lazy"
                                    src={lat && lng
                                        ? `https://maps.google.com/maps?q=${lat},${lng}&z=15&output=embed`
                                        : `https://maps.google.com/maps?q=${encodeURIComponent(formik.values.location.mapUrl)}&z=15&output=embed`
                                    }
                                ></iframe>
                            </div>
                            <small className="text-muted mt-2 d-block">
                                <i className="ri-information-line me-1"></i>
                                This is a live preview based on the link you provided.
                            </small>
                        </div>
                    ) : null}
                </div>
            );
        } else {
            return <Input {...fieldProps} readOnly={field.readOnly} />;
        }
    };

    const renderFormContent = () => (
        <div className="farmhouse-form">
            {serverError && (
                <Alert color="danger" className="mb-3">
                    <i className="ri-error-warning-line me-2"></i>
                    {serverError}
                </Alert>
            )}

            {!isEdit && (
                <Alert color="info" className="mb-3">
                    <i className="ri-information-line me-2"></i>
                    New farmhouse will be created as <strong>Inactive</strong>.
                    After adding amenities and images, you can activate it from the farmhouse details page.
                </Alert>
            )}

            <Form onSubmit={formik.handleSubmit}>
                {FORM_FIELDS.map((section, sectionIndex) => (
                    <div key={sectionIndex} className="mb-4">
                        <h5 className="mb-3">{section.section}</h5>
                        <Row>
                            {section.fields.map((field, fieldIndex) => (
                                <Col md={field.col} key={fieldIndex}>
                                    <div className="mb-3">
                                        <Label htmlFor={field.id} className="form-label">
                                            {field.label}
                                        </Label>
                                        {renderField(field)}
                                        {field.id.includes('.')
                                            ? field.id.split('.').reduce((obj, key) => obj?.[key], formik.touched) &&
                                            field.id.split('.').reduce((obj, key) => obj?.[key], formik.errors) && (
                                                <FormFeedback>
                                                    {field.id.split('.').reduce((obj, key) => obj?.[key], formik.errors)}
                                                </FormFeedback>
                                            )
                                            : formik.touched[field.id] && formik.errors[field.id] && (
                                                <FormFeedback>{formik.errors[field.id]}</FormFeedback>
                                            )
                                        }
                                        {field.id === 'slug' && !isEdit && (
                                            <small className="text-muted">Leave empty to auto-generate</small>
                                        )}
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </div>
                ))}

                {isEdit && (
                    <div className="mb-4 d-flex align-items-center gap-3">
                        <h5 className="mb-0">Status</h5>
                        <SwitchInput
                            id="status"
                            name="status"
                            label=""
                            defaultState={formik.values.status}
                            onText="Active"
                            offText="Inactive"
                            onChange={(checked) => {
                                formik.setFieldValue("status", checked);
                                // Call API to update status
                                handleStatusToggle(checked);
                            }}
                            layout="inline"
                        />
                    </div>
                )}

                <div className="mb-4">
                    <h5 className="mb-3">Pricing</h5>
                    <Row className="mb-2">
                        {PRICE_FIELDS.map((field, idx) => (
                            <Col md={field.col} key={idx}>
                                <Label className="form-label fw-bold">{field.label}</Label>
                            </Col>
                        ))}
                    </Row>
                    {formik.values.priceOptions.map((option, index) => (
                        <Row key={index} className="mb-3">
                            <Col md={3}>
                                <Input
                                    type="text"
                                    disabled
                                    value={getCategoryLabel(option.category)}
                                    className="fw-medium"
                                />
                            </Col>
                            <Col md={3}>
                                <Input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    placeholder="Price"
                                    value={option.price === 0 ? "" : option.price}
                                    onChange={(e) => handlePriceOptionChange(index, "price", e.target.value)}
                                    onFocus={(e) => {
                                        if (e.target.value === "0" || e.target.value === 0 || parseFloat(e.target.value) === 0) {
                                            handlePriceOptionChange(index, "price", "");
                                        }
                                    }}
                                    onBlur={(e) => {
                                        if (e.target.value === "" || e.target.value === null) {
                                            handlePriceOptionChange(index, "price", 0);
                                        }
                                    }}
                                    className="text-end"
                                />
                            </Col>
                            <Col md={3}>
                                <Input
                                    type="number"
                                    min="0"
                                    placeholder="Max People"
                                    value={option.maxPeople === 0 ? "" : option.maxPeople}
                                    onChange={(e) => handlePriceOptionChange(index, "maxPeople", e.target.value)}
                                    onFocus={(e) => {
                                        if (e.target.value === "0" || e.target.value === 0 || parseFloat(e.target.value) === 0) {
                                            handlePriceOptionChange(index, "maxPeople", "");
                                        }
                                    }}
                                    onBlur={(e) => {
                                        if (e.target.value === "" || e.target.value === null) {
                                            handlePriceOptionChange(index, "maxPeople", 0);
                                        }
                                    }}
                                />
                            </Col>
                            <Col md={3}>
                                <Input
                                    type="number"
                                    min="0"
                                    placeholder="Extra Person Price"
                                    value={option.extraPersonPrice === 0 ? "" : option.extraPersonPrice}
                                    onChange={(e) => handlePriceOptionChange(index, "extraPersonPrice", e.target.value)}
                                    onFocus={(e) => {
                                        if (e.target.value === "0" || e.target.value === 0 || parseFloat(e.target.value) === 0) {
                                            handlePriceOptionChange(index, "extraPersonPrice", "");
                                        }
                                    }}
                                    onBlur={(e) => {
                                        if (e.target.value === "" || e.target.value === null) {
                                            handlePriceOptionChange(index, "extraPersonPrice", 0);
                                        }
                                    }}
                                />
                            </Col>
                        </Row>
                    ))}
                </div>

                <div>
                    <h5 className="mb-3">Special Features</h5>
                    <Row>
                        <Col md={6}>
                            <div className="form-check">
                                <Input
                                    type="checkbox"
                                    id="isRecomanded"
                                    name="isRecomanded"
                                    checked={formik.values.isRecomanded}
                                    onChange={(e) => {
                                        formik.setFieldValue("isRecomanded", e.target.checked);
                                        // Uncheck Amazing when Recommended is checked
                                        if (e.target.checked) {
                                            formik.setFieldValue("isAmazing", false);
                                        }
                                    }}
                                    className="form-check-input"
                                />
                                <Label htmlFor="isRecomanded" className="form-check-label">
                                    Recommended Farmhouse
                                </Label>
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="form-check">
                                <Input
                                    type="checkbox"
                                    id="isAmazing"
                                    name="isAmazing"
                                    checked={formik.values.isAmazing}
                                    onChange={(e) => {
                                        formik.setFieldValue("isAmazing", e.target.checked);
                                        // Uncheck Recommended when Amazing is checked
                                        if (e.target.checked) {
                                            formik.setFieldValue("isRecomanded", false);
                                        }
                                    }}
                                    className="form-check-input"
                                />
                                <Label htmlFor="isAmazing" className="form-check-label">
                                    Amazing Deal
                                </Label>
                            </div>
                        </Col>
                    </Row>
                </div>
            </Form>
        </div>
    );

    const renderCreateLocationModal = () => (
        <Modal isOpen={createLocationModalOpen} toggle={() => setCreateLocationModalOpen(false)}>
            <ModalHeader toggle={() => setCreateLocationModalOpen(false)}>
                <i className="ri-map-pin-add-line me-2"></i>
                Create New Location
            </ModalHeader>
            <ModalBody>
                <div className="mb-3">
                    <Label for="newLocationNearby" className="form-label">
                        Nearby <span className="text-danger">*</span>
                    </Label>
                    <Input
                        id="newLocationNearby"
                        type="text"
                        placeholder="Enter nearby location (e.g., Sayan)"
                        value={newLocationData.nearby}
                        onChange={(e) => setNewLocationData({ ...newLocationData, nearby: e.target.value })}
                    />
                    <small className="text-muted">Enter a nearby landmark or area name</small>
                </div>
                <div className="mb-3">
                    <Label for="newLocationCity" className="form-label">
                        City <span className="text-danger">*</span>
                    </Label>
                    <Input
                        id="newLocationCity"
                        type="text"
                        placeholder="Enter city (e.g., Surat)"
                        value={newLocationData.city}
                        onChange={(e) => setNewLocationData({ ...newLocationData, city: e.target.value })}
                    />
                </div>
                <div className="mb-3">
                    <Label for="newLocationState" className="form-label">
                        State <span className="text-danger">*</span>
                    </Label>
                    <Input
                        id="newLocationState"
                        type="text"
                        placeholder="Enter state (e.g., Gujarat)"
                        value={newLocationData.state}
                        onChange={(e) => setNewLocationData({ ...newLocationData, state: e.target.value })}
                    />
                </div>
            </ModalBody>
            <ModalFooter>
                <Button
                    color="secondary"
                    onClick={() => {
                        setCreateLocationModalOpen(false);
                        setNewLocationData({ nearby: "Sayan", city: "Surat", state: "Gujarat" });
                    }}
                    disabled={creatingLocation}
                >
                    Cancel
                </Button>
                <Button
                    color="primary"
                    onClick={handleCreateLocation}
                    disabled={creatingLocation || !newLocationData.nearby || !newLocationData.city || !newLocationData.state}
                >
                    {creatingLocation ? (
                        <>
                            <Spinner size="sm" className="me-2" />
                            Creating...
                        </>
                    ) : (
                        <>
                            <i className="ri-add-line me-2"></i>
                            Create Location
                        </>
                    )}
                </Button>
            </ModalFooter>
        </Modal>
    );

    const renderEditChoiceModal = () => (
        <Modal
            isOpen={currentModal === 'choice'}
            toggle={handleCloseModal}
            centered
            size="md"
        >
            <ModalHeader toggle={handleCloseModal}>
                What would you like to edit?
            </ModalHeader>
            <ModalBody>
                <div className="text-center mb-3">
                    <i className="ri-edit-box-line display-4 text-primary mb-3"></i>
                    <h5>Choose what to update</h5>
                    <p className="text-muted">Select what you want to edit for this farmhouse</p>
                </div>
                <div className="d-grid gap-3">
                    <Button
                        color="primary"
                        onClick={() => handleEditChoice("details")}
                        className="py-3 d-flex align-items-center justify-content-start"
                    >
                        <i className="ri-file-edit-line me-3 fs-4"></i>
                        <div className="text-start">
                            <div className="fw-bold">Update Farmhouse Details</div>
                            <small className="text-white-80">Edit basic info, location, pricing</small>
                        </div>
                    </Button>
                    <Button
                        color="info"
                        onClick={() => handleEditChoice("amenities_images")}
                        className="py-3 d-flex align-items-center justify-content-start"
                    >
                        <i className="ri-image-add-line me-3 fs-4"></i>
                        <div className="text-start">
                            <div className="fw-bold">Update Amenities & Images</div>
                            <small className="text-white-80">Add/remove amenities, upload images</small>
                        </div>
                    </Button>
                </div>
            </ModalBody>
        </Modal>
    );

    const renderPostCreateModal = () => {
        const bedrooms = Array.from({ length: bedroomCount }, (_, i) => i + 1);

        return (
            <Modal
                isOpen={currentModal === 'amenities'}
                toggle={() => {
                    if (isEdit) {
                        handleCloseModal();
                    } else {
                        if (onSuccess) onSuccess();
                    }
                }}
                size="xl"
                centered
                className="full-height-model"
            >
                <ModalHeader toggle={() => {
                    if (isEdit) {
                        handleCloseModal();
                    } else {
                        if (onSuccess) onSuccess();
                    }
                }}>
                    {isEdit ? "Update Amenities & Images" : "Add Amenities & Images"}
                </ModalHeader>
                <ModalBody style={{ maxHeight: '90vh', overflowY: 'auto' }}>
                    {!isEdit && (
                        <div className="alert alert-info mb-3">
                            <i className="ri-information-line me-2"></i>
                            Farmhouse created successfully! Now add amenities and images.
                            The farmhouse is currently <strong>Inactive</strong>.
                            You can activate it after completing these details.
                        </div>
                    )}

                    <h6 className="mb-3">Select Amenities</h6>
                    <Row className="mb-4">
                        {/* Other Amenities Categories */}
                        {Object.entries(AMENITY_GROUPS).map(([category, items]) => {
                            // Skip "Bedroom" if we're showing it separately below
                            if (category === "Bedroom") return null;

                            return (
                                <Col md={6} key={category} className="mb-3">
                                    <h6 className="fw-semibold mb-2">{category}</h6>
                                    <div className="border rounded p-3">
                                        {items && items.map((name) => {
                                            const isChecked = selectedAmenities.some(
                                                (a) => a.name === name && a.category === category
                                            );
                                            const checkboxId = `amenity-${category}-${name}`.replace(/\s+/g, "-");
                                            return (
                                                <div className="form-check mb-2" key={name}>
                                                    <Input
                                                        type="checkbox"
                                                        id={checkboxId}
                                                        className="form-check-input"
                                                        checked={isChecked}
                                                        onChange={(e) => toggleAmenity(category, name, e.target.checked)}
                                                    />
                                                    <Label htmlFor={checkboxId} className="form-check-label">
                                                        {name}
                                                    </Label>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </Col>
                            );
                        })}

                        {/* Dynamic Bedroom Amenities - Separate for each bedroom */}
                        {Array.from({ length: bedroomCount }, (_, i) => i + 1).map((bedroomNumber) => (
                            <Col md={6} key={`bedroom-${bedroomNumber}`} className="mb-3">
                                <h6 className="fw-semibold mb-2">Bedroom {bedroomNumber}</h6>
                                <div className="border rounded p-3">
                                    {AMENITY_GROUPS["Bedroom"] && AMENITY_GROUPS["Bedroom"].length > 0 ? (
                                        AMENITY_GROUPS["Bedroom"].map((amenityName) => {
                                            const category = `Bedroom ${bedroomNumber}`;
                                            const isChecked = selectedAmenities.some(
                                                (a) => a.name === amenityName && a.category === category
                                            );
                                            const checkboxId = `bedroom-${bedroomNumber}-${amenityName}`.replace(/\s+/g, "-");
                                            return (
                                                <div className="form-check mb-2" key={amenityName}>
                                                    <Input
                                                        type="checkbox"
                                                        id={checkboxId}
                                                        className="form-check-input"
                                                        checked={isChecked}
                                                        onChange={(e) => toggleBedroomAmenity(bedroomNumber, amenityName, e.target.checked)}
                                                    />
                                                    <Label htmlFor={checkboxId} className="form-check-label">
                                                        {amenityName}
                                                    </Label>
                                                </div>
                                            );
                                        })
                                    ) : (
                                        <p className="text-muted mb-0">No bedroom amenities configured</p>
                                    )}
                                </div>
                            </Col>
                        ))}
                    </Row>

                    {isEdit && orderedImages && orderedImages.length > 0 && (
                        <div className="mb-4">
                            <div className="d-flex align-items-center justify-content-between mb-3 bg-light p-2 rounded">
                                <h6 className="mb-0">Existing Files ({orderedImages.length}) - {isManualMode ? "Click images 1 by 1 to set order" : "Drag to reorder"}</h6>
                                <div className="d-flex gap-2">
                                    <Button
                                        color={isManualMode ? "danger" : "primary"}
                                        size="sm"
                                        onClick={toggleManualMode}
                                        className="btn-label waves-effect waves-light"
                                    >
                                        <i className={`${isManualMode ? "ri-close-line" : "ri-list-ordered"} label-icon align-middle fs-16 me-2`}></i>
                                        {isManualMode ? "Cancel Selection" : "Set Priority Mode"}
                                    </Button>
                                </div>
                            </div>
                            <DragDropContext onDragEnd={handleOnDragEnd}>
                                <Droppable droppableId="existing-images-droppable" direction="horizontal">
                                    {(provided) => (
                                        <div
                                            className="d-flex flex-wrap gap-3"
                                            {...provided.droppableProps}
                                            ref={provided.innerRef}
                                            style={{ minHeight: '160px', padding: '10px', border: '1px dashed #ced4da', borderRadius: '4px' }}
                                        >
                                            {orderedImages.map((image, index) => {
                                                const imagePath = image.imagePath || '';
                                                const isVideo = imagePath.toLowerCase().match(/\.(mp4|webm|ogg|mov|avi|wmv|flv|mkv)$/i) || image.type === 'video' || image.mimeType?.startsWith('video/');
                                                const isImage = imagePath.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i) || image.type === 'image' || image.mimeType?.startsWith('image/');
                                                const baseUrl = api.API_URL && api.API_URL.endsWith('/') ? api.API_URL.slice(0, -1) : api.API_URL;
                                                const path = imagePath && imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
                                                const imageUrl = `${baseUrl}${path}`;

                                                return (
                                                    <Draggable key={image.id.toString()} draggableId={image.id.toString()} index={index}>
                                                        {(provided, snapshot) => (
                                                            <div
                                                                ref={provided.innerRef}
                                                                {...provided.draggableProps}
                                                                className={`mb-3 ${snapshot.isDragging ? 'dragging-item' : ''}`}
                                                                style={{
                                                                    ...provided.draggableProps.style,
                                                                    zIndex: snapshot.isDragging ? 1000 : 1
                                                                }}
                                                            >
                                                                <div
                                                                    className={`position-relative bg-white rounded shadow-sm border ${isManualMode ? 'cursor-pointer' : ''}`}
                                                                    onClick={() => handleImageClick(image.id)}
                                                                    style={{
                                                                        width: "160px",
                                                                        padding: "5px",
                                                                        transition: snapshot.isDragging ? 'none' : 'transform 0.2s ease',
                                                                        border: isManualMode && manualSequence.includes(image.id) ? '2px solid #00209F' : '1px solid #dee2e6'
                                                                    }}
                                                                >
                                                                    {/* Drag Handle Overlay - Only show if NOT in manual mode */}
                                                                    {!isManualMode && (
                                                                        <div
                                                                            {...provided.dragHandleProps}
                                                                            className="position-absolute top-0 start-0 w-100 h-100 cursor-grab active-grabbing"
                                                                            style={{ zIndex: 2 }}
                                                                            title="Drag to reorder"
                                                                        />
                                                                    )}

                                                                    <div className="position-relative rounded overflow-hidden" style={{ height: "140px" }}>
                                                                        {/* Manual Mode Selection Overlay */}
                                                                        {isManualMode && (
                                                                            <div
                                                                                className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                                                                                style={{
                                                                                    backgroundColor: manualSequence.includes(image.id) ? 'rgba(0, 32, 159, 0.2)' : 'rgba(255, 255, 255, 0.4)',
                                                                                    zIndex: 3
                                                                                }}
                                                                            >
                                                                                <div
                                                                                    className={`rounded-circle d-flex align-items-center justify-content-center fw-bold ${manualSequence.includes(image.id) ? 'bg-primary text-white' : 'bg-white border text-muted'}`}
                                                                                    style={{ width: '40px', height: '40px', fontSize: '18px' }}
                                                                                >
                                                                                    {manualSequence.includes(image.id) ? manualSequence.indexOf(image.id) + 1 : ''}
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                        {isVideo ? (
                                                                            <div className="d-flex align-items-center justify-content-center bg-light h-100">
                                                                                <div className="text-center">
                                                                                    <div className="bg-soft-primary rounded-circle p-2 mb-1 d-inline-block">
                                                                                        <i className="ri-video-line fs-20 text-primary"></i>
                                                                                    </div>
                                                                                    <div className="small text-muted text-truncate px-1" style={{ maxWidth: "140px" }}>
                                                                                        {imagePath.split('/').pop() || 'Video'}
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        ) : isImage ? (
                                                                            <img
                                                                                src={imageUrl}
                                                                                alt="Existing"
                                                                                className="h-100 w-100"
                                                                                style={{ objectFit: "cover" }}
                                                                                onError={(e) => {
                                                                                    e.target.style.display = 'none';
                                                                                    const parent = e.target.parentElement;
                                                                                    parent.innerHTML = `
                                                                                        <div class="d-flex align-items-center justify-content-center bg-light h-100">
                                                                                            <i class="ri-file-line fs-1 text-muted"></i>
                                                                                        </div>
                                                                                    `;
                                                                                }}
                                                                            />
                                                                        ) : (
                                                                            <div className="d-flex align-items-center justify-content-center bg-light h-100">
                                                                                <div className="text-center">
                                                                                    <i className="ri-file-line fs-1 text-muted"></i>
                                                                                </div>
                                                                            </div>
                                                                        )}


                                                                        {/* Delete Button - Needs higher z-index than drag handle */}
                                                                        <Button
                                                                            color="danger"
                                                                            size="sm"
                                                                            className="position-absolute top-0 end-0 m-1 p-0 d-flex align-items-center justify-content-center rounded-circle"
                                                                            style={{ width: "24px", height: "24px", zIndex: 4 }}
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleDeleteExistingImage(image.id);
                                                                            }}
                                                                            title="Delete"
                                                                        >
                                                                            <i className="ri-close-line"></i>
                                                                        </Button>
                                                                    </div>
                                                                    <div className="text-center mt-1 small text-muted text-truncate px-1 fw-medium">
                                                                        Priority: {index + 1}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </Draggable>
                                                );
                                            })}
                                            {provided.placeholder}
                                        </div>
                                    )}
                                </Droppable>
                            </DragDropContext>
                        </div>
                    )}


                    <h6 className="mb-3">Upload Images</h6>
                    <div className="mb-3">
                        <Input
                            type="file"
                            multiple
                            accept="*/*"
                            onChange={handleImageFileChange}
                        />
                        <small className="text-muted">Select images/videos to upload</small>
                    </div>

                    {imageFiles.length > 0 && (
                        <div className="mb-4">
                            <h6 className="mb-2">File Previews ({imageFiles.length})</h6>
                            <div className="d-flex flex-wrap gap-3">
                                {imageFiles.map((file, index) => {
                                    const fileType = fileTypes[index] || 'image';
                                    const fileName = file?.name || `File ${index + 1}`;
                                    const isVideo = fileType === 'video';
                                    const isImage = fileType === 'image';
                                    const preview = imagePreviews[index];
                                    const videoIconPath = require('../../assets/images/video-camera.png');

                                    return (
                                        <div key={index} className="mb-3 position-relative">
                                            <div className="position-relative">
                                                {isVideo ? (
                                                    <div
                                                        className="d-flex align-items-center justify-content-center rounded border"
                                                        style={{
                                                            width: "150px",
                                                            height: "150px",
                                                            backgroundColor: "#f5f5f5",
                                                        }}
                                                    >
                                                        <div className="text-center">
                                                            <div className="bg-soft-primary rounded-circle p-2 mb-2 d-inline-block">
                                                                <i className="ri-video-line fs-24 text-primary"></i>
                                                            </div>
                                                            <div className="small text-muted text-truncate px-2" style={{ maxWidth: "140px" }}>
                                                                {fileName}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ) : (isImage && preview) ? (
                                                    <img
                                                        src={preview}
                                                        alt={`Preview ${index + 1}`}
                                                        className="rounded border"
                                                        style={{
                                                            width: "150px",
                                                            height: "150px",
                                                            objectFit: "cover"
                                                        }}
                                                    />
                                                ) : (
                                                    <div
                                                        className="d-flex align-items-center justify-content-center rounded border"
                                                        style={{
                                                            width: "150px",
                                                            height: "150px",
                                                            backgroundColor: "#f5f5f5",
                                                        }}
                                                    >
                                                        <div className="text-center">
                                                            <i className={`ri-${isImage ? 'image' : 'file'}-line fs-1 text-muted`}></i>
                                                            <div className="small text-muted mt-2 text-truncate px-2" style={{ maxWidth: "140px" }}>
                                                                {fileName}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                                <Button
                                                    color="danger"
                                                    size="sm"
                                                    className="position-absolute top-0 end-0 m-1"
                                                    style={{ padding: "2px 6px" }}
                                                    onClick={() => removeImageFile(index)}
                                                >
                                                    <i className="ri-close-line"></i>
                                                </Button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {!isEdit && (
                        <Alert color="warning">
                            <i className="ri-alert-line me-2"></i>
                            Remember: Farmhouse will remain <strong>Inactive</strong> until you add amenities and images.
                        </Alert>
                    )}
                </ModalBody>
                <ModalFooter className="d-flex justify-content-between align-items-center" style={{ padding: "0rem !important" }}>
                    <div className="w-100">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <Button
                                color="secondary"
                                onClick={() => {
                                    if (isEdit) {
                                        handleCloseModal();
                                    } else {
                                        localStorage.removeItem(STORAGE_KEY);
                                        if (onSuccess) onSuccess();
                                    }
                                }}
                            >
                                {isEdit ? "Cancel" : "Skip for Now"}
                            </Button>
                            <Button
                                color="primary"
                                onClick={handleSaveAmenitiesAndImages}
                                disabled={savingAmenities || uploadingImages}
                            >
                                {savingAmenities || uploadingImages ? (
                                    <>
                                        <Spinner size="sm" className="me-2" />
                                        Saving...
                                    </>
                                ) : (
                                    `Save ${isEdit ? 'Updates' : 'Amenities & Images'}`
                                )}
                            </Button>
                        </div>
                    </div>
                </ModalFooter>
            </Modal>
        );
    };

    const renderFormModal = () => {
        const handleSubmitButtonClick = () => {
            formik.handleSubmit();
        };

        return (
            <Modal
                isOpen={currentModal === 'form'}
                toggle={handleCloseModal}
                size="xl"
                centered
                className="full-height-model"
            >
                <ModalHeader toggle={handleCloseModal}>
                    {isEdit ? "Edit Farmhouse Details" : "Create New Farmhouse"}
                </ModalHeader>
                <ModalBody style={{ maxHeight: '90vh', overflowY: 'auto' }}>
                    {renderFormContent(false)}
                </ModalBody>
                <ModalFooter className="d-flex justify-content-between align-items-center">
                    <div className="w-100">
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <Button
                                    color="secondary"
                                    onClick={handleCloseModal}
                                >
                                    <i className="ri-arrow-left-line me-1"></i>
                                    Cancel
                                </Button>
                            </div>
                            <div className="d-flex gap-2">
                                <Button color="light" onClick={() => {
                                    formik.resetForm();
                                    setSelectedAmenities([]);
                                    localStorage.removeItem(STORAGE_KEY);
                                }}>
                                    Reset Form
                                </Button>
                                <Button
                                    color="primary"
                                    onClick={handleSubmitButtonClick}
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <>
                                            <Spinner size="sm" className="me-2" />
                                            {isEdit ? "Updating..." : "Creating..."}
                                        </>
                                    ) : (
                                        <>
                                            <i className={isEdit ? "ri-save-line me-1" : "ri-add-line me-1"}></i>
                                            {isEdit ? "Update Farmhouse" : "Create Farmhouse"}
                                        </>
                                    )}
                                </Button>
                            </div>
                        </div>
                    </div>
                </ModalFooter>
            </Modal>
        );
    };

    // For modal mode
    if (isModal) {
        return (
            <>
                {currentModal === 'choice' && renderEditChoiceModal()}
                {currentModal === 'form' && renderFormModal()}
                {currentModal === 'amenities' && renderPostCreateModal()}
                {renderCreateLocationModal()}
            </>
        );
    }

    // Full page view (non-modal)
    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title={isEdit ? "Edit Farmhouse" : "Create Farmhouse"} pageTitle="Farmhouses" />
                    <Row>
                        <Col lg={12}>
                            <Card>
                                <CardHeader>
                                    <h4 className="card-title mb-0">Farmhouse Details</h4>
                                </CardHeader>
                                <CardBody>
                                    {renderFormContent()}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
            {renderPostCreateModal()}
            {renderEditChoiceModal()}
            {renderCreateLocationModal()}
        </React.Fragment>
    );
};

export default FarmhouseCreate;